import { mapGetters } from 'vuex'
import { cloneDeep, filter, find, includes, isEmpty, map, take, toLower, trim } from 'lodash'

export default {
  props: {
    smartPointsGetter: {
      type: String,
      require: true
    },
    forceInit: {
      type: Boolean,
      default: false,
      require: false
    },
    maxResultsProp: {
      type: Number,
      default: 20,
      require: false
    },
    maxResultsAfterSearch: {
      type: Number,
      default: 20,
      require: false
    },
    useClusters: {
      type: Boolean,
      default: false,
      require: false
    }
  },
  data () {
    return {
      mobileSelectionExpanded: false
    }
  },
  computed: {
    ...mapGetters([
      'googleService',
      'getGoogleMapLoading',
      'getCurrentUserGeolocation'
    ]),
    searchInput: {
      get () {
        return this.$store.getters.getSearchInputSmartPoints
      },
      set (value) {
        this.$store.dispatch('setSearchInputSmartPoints', value)
      }
    },
    maxResults: {
      get () {
        return this.$store.getters.getMaxResultsSmartPoints
      },
      set (value) {
        this.$store.dispatch('setMaxResultsSmartPoints', value)
      }
    },
    closestSmartPoints: {
      get () {
        return this.$store.getters.getClosestSmartPoints
      },
      set (value) {
        this.$store.dispatch('setClosestSmartPoints', value)
      }
    },
    cachedFilteredSearchedClosestSmartPoints: {
      get () {
        return this.$store.getters.getCachedFilteredSearchedClosestSmartPoints
      },
      set (value) {
        this.$store.dispatch('setCachedFilteredSearchedClosestSmartPoints', value)
      }
    },
    smartPoints () {
      return this.$store.getters[this.smartPointsGetter]
    },
    filteredSearchInput () {
      return trim(toLower(this.searchInput))
    },
    filteredSearchedSmartPoints () {
      return filter(cloneDeep(this.smartPoints), function (smartPoint) {
        const searchable = this.getSmartPointSearchable(smartPoint)
        return includes(searchable, this.filteredSearchInput)
      }.bind(this))
    },
    filteredSearchedClosestSmartPoints () {
      const closestPoints = map(this.closestSmartPoints, closestSmartPoint => closestSmartPoint.sellingPoint)
      return take(closestPoints, this.maxResults)
    }
  },
  watch: {
    getCurrentUserGeolocation (val) {
      if ('findClosest' in this.googleService) {
        this.googleService.findClosest(val, this.$store.getters[this.smartPointsGetter], this.maxResults)
      }
    }
  },
  methods: {
    async searchSmartPoints () {
      this.setAllSmartPointsUnchecked()
      if (isEmpty(this.filteredSearchInput)) { return }
      return await this.googleService.findClosest(this.filteredSearchInput, this.smartPoints, this.maxResultsAfterSearch)
    },
    getSmartPointSearchable (smartPoint) {
      const searchable = `${smartPoint.name} ${smartPoint.address} ${smartPoint.phone} ${smartPoint.postal} ${smartPoint.city} ${smartPoint.country}`
      return toLower(searchable)
    },
    getSmartPointById (smartPointId) {
      return find(this.smartPoints, (smartPoint) => smartPoint.id * 1 === smartPointId)
    },
    getSmartPointById2 (smartPointId) {
      return find(this.filteredSearchedSmartPoints, (smartPoint) => smartPoint.id * 1 === smartPointId)
    },
    findSmartPointByName (inputName) {
      return find(this.smartPoints, (smartPoint) => includes(smartPoint.name, inputName))
    },
    getDistanceInMeters (smartPoint) {
      const smartPointFound = find(this.closestSmartPoints, (closestSmartPoint) => {
        return closestSmartPoint.sellingPoint === smartPoint
      })
      if (smartPointFound !== undefined) {
        return smartPointFound.distance
      }
    },
    formatDistance (distance) {
      if (distance > 1000) {
        const distanceInKm = distance / 1000
        return distanceInKm.toFixed(2) + 'km'
      }
      return Math.round(distance) + 'm'
    },
    async setSmartPointChecked (smartPoint) {
      await Promise.all([
        this.setAllSmartPointsUnchecked(),
        this.cachedFilteredSearchedClosestSmartPoints = map(this.cachedFilteredSearchedClosestSmartPoints, point =>
          point.id === smartPoint.id ? { ...point, checked: true } : point
        ),
        smartPoint.checked = true,
        this.$store.dispatch('setSelectedSmartPoint', smartPoint),
        this.googleService.updateMap(this.filteredSearchedClosestSmartPoints, smartPoint),
        this.$toasted.success(this.t('pickup.point.selected'), { duration: '2500' })
      ])
    },
    getSmartPointChecked (smartPoint) {
      return !!smartPoint.checked
    },
    setAllSmartPointsUnchecked () {
      this.cachedFilteredSearchedClosestSmartPoints = map(this.filteredSearchedClosestSmartPoints, (smartPoint) => {
        smartPoint.checked = false
        return smartPoint
      })
    },
    manageExpand () {
      this.mobileSelectionExpanded = !this.mobileSelectionExpanded
    },
    async resetMap () {
      await this.googleService.updateMap(take(this.filteredSearchedClosestSmartPoints, this.maxResults))
    },
    async initializeGoogleService () {
      await this.$store.dispatch('initializeGoogleService', this.useClusters)
    }
  },
  async mounted () {
    this.maxResults = this.maxResultsProp
    this.$root.$on('findClosest', async function (data) {
      this.closestSmartPoints = data.sellingPoints
      await this.googleService.updateMap(take(this.filteredSearchedClosestSmartPoints, data.maxResults))
      this.setAllSmartPointsUnchecked()
    }.bind(this))

    if (this.forceInit) {
      await this.initializeGoogleService()
      await Promise.all([
        this.googleService.initMapPoints(this.$store.getters[this.smartPointsGetter]),
        this.googleService.findClosest(this.getCurrentUserGeolocation, this.$store.getters[this.smartPointsGetter], this.maxResults)
      ])
    }
  },
  created () {
    this.$root.$on('search-smart-points', this.searchSmartPoints)
  }
}
